import { keyBy } from 'lodash';
import axios from 'axios';
import { types, flow, getRoot, Instance } from 'mobx-state-tree';
import QueryString from 'qs';

import { getCompanyDomain } from '@src/helpers';
import * as JobsAPI from '@src/services/jobs';
import * as AnonymousJobsAPI from '@src/services/anonymousJobs';
import { USER_ROLES } from 'src/constants';
import { toast } from 'src/components';
import { getJobId, formatJobToUnifiedShape } from '../../helpers';
import { JobModel, IJob } from './JobModel';

export const JobsStore = types
  .model('Jobs', {
    isFetching: 0,
    isPostingJob: false,
    jobs: types.map(JobModel)
  })
  .actions(self => {
    const companySlug = getCompanyDomain();
    const CancelToken = axios.CancelToken;
    let source = CancelToken.source();

    return {
      initTree: function() {
        self.isPostingJob = false;
        self.isFetching = 0;
      },
      fetchJob: flow(function*(jobId) {
        self.isFetching = self.isFetching + 1;
        try {
          const response = yield JobsAPI.getCompanyJob(companySlug, jobId, { cancelToken: source.token });
          const job = self.jobs.get(jobId) || {};
          self.jobs.set(jobId, { ...job, ...formatJobToUnifiedShape(response) });
        } finally {
          self.isFetching = self.isFetching - 1;
        }
      }),
      fetchAnonymousJob: flow(function*(jobId) {
        self.isFetching = self.isFetching + 1;
        try {
          const response = yield JobsAPI.getJob(jobId, { cancelToken: source.token });
          const job = self.jobs.get(jobId) || {};
          self.jobs.set(jobId, { ...job, ...formatJobToUnifiedShape(response) });
        } finally {
          self.isFetching = self.isFetching - 1;
        }
      }),
      createJob: flow(function*(job) {
        try {
          self.isPostingJob = true;
          const response = yield JobsAPI.createJob(companySlug, job);
          self.jobs.set(response.entity.id, { ...response.entity, accessPolicies: response.policies });
          return response;
        } catch (err) {
          return {};
        } finally {
          self.isPostingJob = false;
        }
      }),
      updateJob: flow(function*(job) {
        self.isPostingJob = true;
        try {
          const response = yield JobsAPI.updateJob({ companySlug, ...job });
          self.jobs.set(response.entity.id, response.entity);
          return response;
        } finally {
          self.isPostingJob = false;
        }
      }),
      getJobAccessPolicies: flow(function*(jobId) {
        self.isFetching = self.isFetching + 1;
        try {
          const accessPolicies = yield JobsAPI.getJobAccessPolicies(
            { companySlug, jobId },
            { cancelToken: source.token }
          );
          const job = self.jobs.get(jobId) || {};
          self.jobs.set(jobId, { ...job, accessPolicies });
        } finally {
          self.isFetching = self.isFetching - 1;
        }
      }),
      getJobApplicationSettings: flow(function*(jobId) {
        self.isFetching = self.isFetching + 1;
        try {
          const { application_form_settings, external_url } = yield JobsAPI.getJobApplicationSettings(
            { companySlug, jobId },
            { cancelToken: source.token }
          );

          const job = self.jobs.get(jobId) || {};
          self.jobs.set(jobId, {
            ...job,
            application_form_settings: {
              ...application_form_settings,
              external_url,
              custom_questions: Object.values(application_form_settings.custom_questions || {})
            }
          });
        } finally {
          self.isFetching = self.isFetching - 1;
        }
      }),
      updateJobApplicationSettings: flow(function*(payload) {
        const response = yield JobsAPI.updateJobApplicationSettings({ companySlug, ...payload });
        return response;
      }),
      getJobAdvertising: flow(function*({ jobId }) {
        self.isFetching = self.isFetching + 1;
        try {
          const response = yield JobsAPI.getJobAdvertising({ companySlug, jobId }, { cancelToken: source.token });
          const job = self.jobs.get(jobId) || {};
          self.jobs.set(jobId, { ...job, advertising: response });
        } finally {
          self.isFetching = self.isFetching - 1;
        }
      }),
      updateJobAdvertising: flow(function*({ jobId, ...payload }) {
        const response = yield JobsAPI.updateJobAdvertising({ companySlug, jobId, ...payload });
        const job = self.jobs.get(jobId);
        self.jobs.set(jobId, { ...job, advertising: response });
      }),
      getJobFeeds: flow(function*({ jobId }) {
        self.isFetching = self.isFetching + 1;
        try {
          const response = yield JobsAPI.getJobFeeds({ companySlug, jobId }, { cancelToken: source.token });
          const job = self.jobs.get(jobId) || {};
          self.jobs.set(jobId, { ...job, feeds: response });
        } finally {
          self.isFetching = self.isFetching - 1;
        }
      }),
      updateJobFeeds: flow(function*({ jobId, ...payload }) {
        const response = yield JobsAPI.updateJobFeeds({ companySlug, jobId, ...payload });
        const job = self.jobs.get(jobId);
        job.feeds = response;
        return response;
      }),
      deleteJobFeeds: flow(function*({ jobId, data }) {
        const response = yield JobsAPI.deleteJobFeeds({ companySlug, jobId }, { data });
        const job = self.jobs.get(jobId);
        job.feeds = response;
        return response;
      }),
      getJobMembers: flow(function*({ jobId }) {
        self.isFetching = self.isFetching + 1;
        try {
          const { company_users, job_users } = yield JobsAPI.getJobMembers(
            { companySlug, jobId },
            { cancelToken: source.token }
          );
          const job = self.jobs.get(jobId) || {};
          self.jobs.set(jobId, { ...job, company_users, job_users });
        } finally {
          self.isFetching = self.isFetching - 1;
        }
      }),
      updateJobMembers: flow(function*({ jobId, ...payload }) {
        const { job_users } = yield JobsAPI.updateJobMembers({ companySlug, jobId, ...payload });
        const job = self.jobs.get(jobId);
        job.job_users = job_users;
      }),
      getJSS: flow(function*(jobId) {
        self.isFetching = self.isFetching + 1;
        try {
          const suggestionSettings = yield JobsAPI.getJSS({ companySlug, jobId }, { cancelToken: source.token });
          const job = self.jobs.get(jobId) || {};
          self.jobs.set(jobId, { ...job, suggestionSettings });
        } finally {
          self.isFetching = self.isFetching - 1;
        }
      }),
      updateJSS: flow(function*({ jobId, ...payload }) {
        const { entity } = yield JobsAPI.updateJSS({ companySlug, jobId, ...payload });
        const job = self.jobs.get(jobId);
        job.suggestionSettings = entity;
      }),
      uploadCV: flow(function*(...args) {
        const response = yield JobsAPI.uploadCV(...args);
        return response;
      }),
      uploadPhoto: flow(function*(...args) {
        const response = yield JobsAPI.uploadPhoto(...args);
        return response;
      }),
      postAndParseCV: flow(function*(...args) {
        const { userStore } = getRoot(self);
        const { filename: resume_filename, resume_url } = yield AnonymousJobsAPI.uploadCV(...args);
        const {
          languages_attributes,
          experiences_attributes,
          degrees_attributes,
          ...parsedInfoFromCV
        } = yield AnonymousJobsAPI.parseCV({ payload: { resume_url } });
        userStore.setUserInfo({
          role: USER_ROLES.candidate,
          resume_url,
          resume_updated_at: new Date().toISOString(),
          resume_filename,
          languages: languages_attributes,
          experiences: experiences_attributes,
          degrees: degrees_attributes,
          ...parsedInfoFromCV
        });
      }),
      applyForJob: flow(function*(job, rawPayload, params) {
        const { authStore, userStore, routerStore } = getRoot(self);

        const { standalone, from_careers_page } =
          QueryString.parse(routerStore.location.search, { ignoreQueryPrefix: true }) || {};

        const fromCareersPage = from_careers_page || standalone;
        let response;
        const payload = { ...rawPayload, ...(fromCareersPage && { from_careers_page: true }) };
        if (job.is_anonymous) {
          response = yield AnonymousJobsAPI.applyForJob({ jobId: job.cid, payload }, params);
        } else {
          response = yield JobsAPI.applyForJob({ jobId: job.cid, payload }, params);
        }
        const currentJob = self.jobs.get(job.cid) || {};
        self.jobs.set(job.cid, { ...currentJob, is_applied: true });
        userStore.setUserInfo(response.user);
        if (!authStore.isAuthorized) {
          authStore.setIsAuthorized(true);
        }
      }),
      simpleApplyForJob: flow(function*(job) {
        self.isFetching = self.isFetching + 1;

        const { authStore, routerStore } = getRoot(self);
        if (!authStore.isAuthorized) return;

        // same as at applyForJob method
        const { standalone, from_careers_page } =
          QueryString.parse(routerStore.location.search, { ignoreQueryPrefix: true }) || {};

        const fromCareersPage = from_careers_page || standalone;

        try {
          let response;
          if (job.is_anonymous) {
            response = yield AnonymousJobsAPI.simpleApplyForJob({
              jobId: getJobId(job),
              from_careers_page: fromCareersPage
            });
          } else {
            response = yield JobsAPI.simpleApplyForJob({ jobId: getJobId(job), from_careers_page: isStandalone });
          }
          const currentJob = self.jobs.get(job.cid) || {};
          self.jobs.set(job.cid, { ...currentJob, is_applied: true });
          if (response.external_url) {
            window.location.href = response.external_url;
          } else {
            self.isFetching = self.isFetching - 1;
          }
        } catch (error) {
          self.isFetching = self.isFetching - 1;
        } finally {
        }
      }),
      checkForJobApplication: flow(function*({ jobId }) {
        self.isFetching = self.isFetching + 1;
        try {
          const { applied } = yield JobsAPI.checkForJobApplication({ jobId });
          const job = self.jobs.get(jobId) || {};
          self.jobs.set(jobId, { ...job, is_applied: applied });
        } catch (error) {
        } finally {
          self.isFetching = self.isFetching - 1;
        }
      }),
      uploadCustomFileAnswer: flow(function*({ jobId, file }) {
        try {
          let response = { file_url: null };
          const job = (self.jobs.get(jobId) as IJob) || ({} as IJob);
          if (job.is_anonymous) {
            response = yield AnonymousJobsAPI.uploadCustomFileAnswer({ file });
          } else {
            response = yield JobsAPI.uploadCustomFileAnswer({ jobId, file });
          }

          return response.file_url;
        } catch (error) {
          toast.error('Error uploading file.');
        } finally {
        }
      }),

      abort: function() {
        source.cancel('Operation canceled by the user.');
        source = CancelToken.source();
      }
    };
  })
  .views(self => ({
    getJobById(id: string) {
      return self.jobs.get(id);
    },
    getJob(job: IJob) {
      return this.getJobById(getJobId(job));
    },
    getJobPreference(id: string) {
      const preference = this.getJobById(id)?.suggestionSettings;
      return (
        preference && {
          ...preference,
          sub_themes: keyBy(preference.sub_themes, 'theme_id'),
          focuses: keyBy(preference.focuses, 'theme_id')
        }
      );
    }
  }));

export type IJobsStore = Instance<typeof JobsStore>;
